import React, { useEffect } from 'react';
import type { GridAlignment, GridColumns } from '@mui/x-data-grid';
import { Avatar, Box, Chip, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import moment from 'moment';
import DownloadIcon from '@mui/icons-material/Download';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import { fetchWorkerWorkDone } from '../../services/workeranalytics.service';
import { downloadAnalytics } from '../../services/project.service';
import { getOffset } from '../../utils/utilityFunctions';
import { parseWorkerWorkBreakdown } from '../../utils/workerAnalyticsMath';

const countLabel = (value: unknown): string => {
	const count = Number(value) || 0;
	return `${count} ${count === 1 ? 'record' : 'records'}`;
};

const initials = (name: string): string =>
	name
		.split(/\s+/)
		.filter(Boolean)
		.slice(0, 2)
		.map((part) => part[0]?.toUpperCase())
		.join('');

const formatMoney = (value: unknown): string =>
	new Intl.NumberFormat('en-SG', {
		style: 'currency',
		currency: 'SGD',
		minimumFractionDigits: 0,
		maximumFractionDigits: 2,
	}).format(Number(value) || 0);

interface WorkKindSummaryCellProps {
	kind: 'HIP' | 'Non-HIP';
	recordCount: unknown;
	breakdown: unknown;
	colors: { background: string; foreground: string; hover: string };
}

const WorkKindSummaryCell = ({ kind, recordCount, breakdown, colors }: WorkKindSummaryCellProps) => {
	const details = parseWorkerWorkBreakdown(breakdown);
	return (
		<Tooltip
			arrow
			placement='top-start'
			componentsProps={{
				tooltip: {
					sx: {
						p: 1.5,
						width: 320,
						maxWidth: 360,
						bgcolor: '#172033',
						borderRadius: 2,
						boxShadow: '0 14px 32px rgba(15, 23, 42, 0.24)',
					},
				},
			}}
			title={
				<Box>
					<Typography variant='subtitle2' sx={{ color: '#fff', fontWeight: 750, mb: 1 }}>
						{kind} work details
					</Typography>
					<Stack spacing={0.9}>
						{details.length > 0 ? (
							details.map((detail, index) => (
								<Box
									key={`${detail.workDoneId}-${index}`}
									sx={{
										pt: index === 0 ? 0 : 1,
										borderTop: index === 0 ? 'none' : '1px solid rgba(255,255,255,0.12)',
									}}>
									<Typography variant='body2' sx={{ color: '#fff', fontWeight: 650 }}>
										{detail.typeName}
									</Typography>
									<Typography variant='caption' sx={{ color: '#cbd5e1' }}>
										{detail.projectName} · {detail.creditedQuantity} credited
									</Typography>
									{detail.collaboratorNames && (
										<Stack direction='row' spacing={0.6} alignItems='center' sx={{ mt: 0.35 }}>
											<GroupsOutlinedIcon sx={{ fontSize: 15, color: '#c4b5fd' }} />
											<Typography variant='caption' sx={{ color: '#e2e8f0' }}>
												Worked with {detail.collaboratorNames}
											</Typography>
										</Stack>
									)}
								</Box>
							))
						) : (
							<Typography variant='caption' sx={{ color: '#cbd5e1' }}>
								No {kind} work in this period.
							</Typography>
						)}
					</Stack>
				</Box>
			}>
			<Box
				sx={{
					minWidth: 0,
					width: '100%',
					px: 0.75,
					py: 0.55,
					mx: -0.75,
					display: 'flex',
					alignItems: 'flex-start',
					border: '1px solid transparent',
					borderRadius: 2,
					cursor: 'help',
					transition: 'background-color 120ms ease, border-color 120ms ease',
					'&:hover': { bgcolor: colors.hover, borderColor: colors.background },
				}}>
				<Chip
					label={countLabel(recordCount)}
					size='small'
					sx={{ height: 24, bgcolor: colors.background, color: colors.foreground, fontWeight: 700 }}
				/>
			</Box>
		</Tooltip>
	);
};

export function ManageWorkerAnalytics(props: any) {
	const {
		loadDataToGrid,
		page,
		setPage,
		pageSize,
		setPageSize,
		fromDate,
		toDate,
		loading,
		setLoading,
		workerAnalyticsData,
		workerAnalyticsTotalLength,
	} = props;

	const download = async (id: number, workerName: string) => {
		const result: any = await fetchWorkerWorkDone(
			id,
			moment(fromDate)
				.startOf('day')
				.utcOffset(getOffset() * -1)
				.format('YYYY-MM-DD HH:mm:ss'),
			moment(toDate)
				.endOf('day')
				.utcOffset(getOffset() * -1)
				.format('YYYY-MM-DD HH:mm:ss')
		);
		const uniqueWorkerDetails = result.data?.data;
		if (fromDate && toDate) {
			const excel = await downloadAnalytics(
				id,
				uniqueWorkerDetails,
				moment(toDate)
					.endOf('day')
					.utcOffset(getOffset() * -1)
					.format('YYYY-MM-DD HH:mm:ss'),
				moment(fromDate)
					.startOf('day')
					.utcOffset(getOffset() * -1)
					.format('YYYY-MM-DD HH:mm:ss')
			);
			setLoading(true);

			if (excel.status === 200) {
				const a = document.createElement('a');
				a.href = URL.createObjectURL(new Blob([excel.data], { type: 'application/xlsx' }));
				a.download = `${workerName}-WorkDone-${moment(fromDate).format('DDMMYY')}-${moment(toDate).format(
					'DDMMYY'
				)}.xlsx`;
				a.click();
			}
		}
		setLoading(false);
	};

	const columns: GridColumns = [
		{
			field: 'workerName',
			headerName: 'Worker',
			flex: 1.25,
			minWidth: 180,
			align: 'left' as GridAlignment,
			headerAlign: 'left' as GridAlignment,
			renderCell: (params: any) => (
				<Stack direction='row' alignItems='center' spacing={1.25} sx={{ minWidth: 0 }}>
					<Avatar sx={{ width: 34, height: 34, bgcolor: '#e8f0fb', color: '#245b9b', fontSize: 13, fontWeight: 750 }}>
						{initials(params.value || '')}
					</Avatar>
					<Typography variant='body2' sx={{ fontWeight: 700 }} noWrap>
						{params.value}
					</Typography>
				</Stack>
			),
		},
		{
			field: 'workDoneEntriesCount',
			headerName: 'Total work',
			flex: 1,
			minWidth: 145,
			renderCell: (params: any) => (
				<Typography variant='body2' sx={{ fontWeight: 700 }}>
					{countLabel(params.value)}
				</Typography>
			),
		},
		{
			field: 'hipWorkDoneEntries',
			headerName: 'HIP work',
			flex: 1.35,
			minWidth: 205,
			renderCell: (params: any) => (
				<WorkKindSummaryCell
					kind='HIP'
					recordCount={params.value}
					breakdown={params.row.hipWorkBreakdown}
					colors={{ background: '#e8f0fb', foreground: '#245b9b', hover: '#f4f8fd' }}
				/>
			),
		},
		{
			field: 'nonHipWorkDoneEntries',
			headerName: 'Non-HIP work',
			flex: 1.35,
			minWidth: 215,
			renderCell: (params: any) => (
				<WorkKindSummaryCell
					kind='Non-HIP'
					recordCount={params.value}
					breakdown={params.row.nonHipWorkBreakdown}
					colors={{ background: '#e7f6f2', foreground: '#146c5a', hover: '#f3faf8' }}
				/>
			),
		},
		{
			field: 'revenue',
			headerName: 'HIP revenue',
			width: 125,
			align: 'right' as GridAlignment,
			headerAlign: 'right' as GridAlignment,
			renderCell: (params: any) => (
				<Typography variant='body2' sx={{ fontWeight: 700, color: '#334155' }}>
					{formatMoney(params.value)}
				</Typography>
			),
		},
		{
			field: 'download',
			headerName: 'Report',
			width: 76,
			align: 'center' as GridAlignment,
			headerAlign: 'center' as GridAlignment,
			sortable: false,
			filterable: false,
			renderCell: (params: any) => (
				<Tooltip title={`Download ${params.row.workerName}'s work report`}>
					<IconButton
						size='small'
						aria-label={`Download ${params.row.workerName} work done`}
						onClick={() => download(params.row.id, params.row.workerName)}
						sx={{ color: '#475569', border: '1px solid #dbe3ec', borderRadius: 2 }}>
						<DownloadIcon fontSize='small' />
					</IconButton>
				</Tooltip>
			),
		},
	];

	useEffect(() => {
		loadDataToGrid(page, pageSize);
	}, [page]);

	const handlePageChange = (pageNumber: number) => {
		setPage(pageNumber);
		loadDataToGrid(pageNumber, pageSize);
	};

	const handlePageSizeChange = (size: number) => {
		setPageSize(size);
		loadDataToGrid(page, size);
	};

	return (
		<Box sx={{ flex: 1, minWidth: 0 }}>
			<Box
				sx={{
					mb: 1.5,
					display: 'flex',
					alignItems: { xs: 'flex-start', md: 'center' },
					justifyContent: 'space-between',
					flexDirection: { xs: 'column', md: 'row' },
					gap: 1,
				}}>
				<Box>
					<Typography variant='h6' sx={{ fontWeight: 750, letterSpacing: '-0.01em' }}>
						Worker breakdown
					</Typography>
					<Typography variant='body2' color='text.secondary'>
						Approved work credited to each worker for the selected period.
					</Typography>
				</Box>
				<Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
					<Chip label='HIP · claim-related' size='small' sx={{ bgcolor: '#e8f0fb', color: '#245b9b' }} />
					<Chip label='Non-HIP · analytics only' size='small' sx={{ bgcolor: '#e7f6f2', color: '#146c5a' }} />
				</Stack>
			</Box>

			<Box sx={{ border: '1px solid #dfe6ee', borderRadius: 3, overflow: 'hidden', bgcolor: '#fff' }}>
				<DataGrid
					autoHeight
					rows={workerAnalyticsData}
					columns={columns}
					loading={loading}
					rowCount={workerAnalyticsTotalLength}
					pagination
					paginationMode='server'
					pageSize={pageSize}
					rowsPerPageOptions={[10, 20, 50]}
					page={page}
					onPageSizeChange={handlePageSizeChange}
					onPageChange={handlePageChange}
					disableSelectionOnClick
					rowHeight={72}
					headerHeight={52}
					hideFooter={workerAnalyticsTotalLength <= pageSize}
					localeText={{
						noRowsLabel: fromDate === '' || toDate === '' ? 'Choose a work period' : 'No approved work in this period',
					}}
					sx={{
						border: 0,
						'& .MuiDataGrid-columnHeaders': { bgcolor: '#f8fafc', borderBottom: '1px solid #dfe6ee' },
						'& .MuiDataGrid-columnHeaderTitle': { fontWeight: 750, color: '#475569', fontSize: 13 },
						'& .MuiDataGrid-columnSeparator': { display: 'none' },
						'& .MuiDataGrid-cell': { px: 1.5, borderColor: '#edf1f5' },
						'& .MuiDataGrid-row:hover': { bgcolor: '#f8fbff' },
						'& .MuiDataGrid-footerContainer': { borderTop: '1px solid #dfe6ee' },
					}}
				/>
			</Box>
		</Box>
	);
}
